import { useState } from "react";
import { OutfitBlock, WeaponBlock, WeaponBlockBase } from "../../../model/character";
import { v4 } from 'uuid';

import { Box, Button, Chip, FormGroup, Stack, TextField, Typography } from "@mui/material";
import { NumberField } from "../../../components/formFields/NumberField";
import FormFieldLabel from "../../../components/formFields/FormFieldLabel";
import { ModalContentSection } from "../../../components/Modal";

import { useNavigate, useParams } from "react-router-dom";
import { exportData, loadLibraryData, saveLibraryData } from "../../../master/libraryData";
import AbilityEditor from "../../characterPage/AbilityEditor";
import Page from "../../../components/Page";
import ButtonFlat from "../../../components/ButtonFlat";
import { StatDisplay, StatDisplayExtra } from "../../../components/StatDisplay";

type Block = WeaponBlock | WeaponBlockBase;

const defaultBlock: OutfitBlock = {
    id: "",
    name: "Outfit",
    slots: 0,
    armor: 0,
    armorAtZero: 0,
    defence: 0,
    endurance: 0,
    outfitType: [],
    addFeatures: [],
    removeFeatures: [],
    passives: [],
    strongHits: [],
    resources: 0,
    knowledge: 0,
    influence: 0,
    acquire: 0,
    notes: "",
    size: "standard"
}

export const GetNew = (name: string): OutfitBlock => {
    return {
        ...defaultBlock,
        id: v4(),
        name: name
    }
}

export interface OutfitEditorProps {
    prefix: string;
    pathType: string;
    defaultName: string;
    isModifier?: boolean
}

export const OutfitBlockEditor = (props: {outfitBlock: OutfitBlock, setOutfitBlock: (outfitBlock: OutfitBlock) => void, isModifier?: boolean}) => {
    const { outfitBlock, setOutfitBlock } = props;
    const isModifier = props.isModifier === true;
    const [ feature, setFeature ] = useState("");
    const [ outfitType, setOutfitType ] = useState("");

    const onChange = (key: string, value: any) => {
        setOutfitBlock({
            ...outfitBlock,
            [key]: value
        })
    }

    const handleAddFeature = () => {
        const newFeatures = [ ...outfitBlock.addFeatures, feature ];
        onChange("addFeatures", newFeatures);
        setFeature("");
    }

    const handleRemoveFeature = (index: number) => {
        const features = [ ...outfitBlock.addFeatures ];
        features.splice(index, 1);
        onChange("addFeatures", features);
    }

    const handleAddType = () => {
        const current = [ ...outfitBlock.outfitType, outfitType ];
        onChange("outfitType", current);
        setOutfitType("");
    }

    const handleRemoveType = (index: number) => {
        const current = [ ...outfitBlock.outfitType ];
        current.splice(index, 1);
        onChange("outfitType", current);
    }

    return <>
        <Stack spacing={1}>
            <FormFieldLabel label="Name">
                <TextField fullWidth type="text" variant="standard" value={outfitBlock.name} onChange={(event) => onChange("name", event.currentTarget.value)} />
            </FormFieldLabel>
            {/* <SelectField value={current.size} onChange={(value) => onChange("size", value)} options={[
                { name: `Size - None (0)`, value: "none" },
                { name: "Size - Trinket (0.25)", value: "trinket" },
                { name: "Size - Small (1)", value: "small" } ,
                { name: "Size - Standard (2)", value: "standard" } ,
                { name: "Size - Large (3)", value: "large" } ,
            ]} /> */}
            <FormFieldLabel label="Defence" justifyContent="end">
                <NumberField step={1} value={outfitBlock.defence} onChange={(value) => onChange("defence", value)} customRenderer={(v) => StatDisplayExtra(v)} />
            </FormFieldLabel>
            <FormFieldLabel label="Endurance" justifyContent="end">
                <NumberField step={1} value={outfitBlock.endurance} onChange={(value) => onChange("endurance", value)} customRenderer={(v) => StatDisplayExtra(v)} />
            </FormFieldLabel>
            <FormFieldLabel label="Armour" justifyContent="end">
                <NumberField step={1} value={outfitBlock.armor} onChange={(value) => onChange("armor", value)} customRenderer={(v) => StatDisplayExtra(v, { showModifier: isModifier })} />
            </FormFieldLabel>
            <FormFieldLabel label="Armour at 0 Endurance" justifyContent="end">
                <NumberField step={1} value={outfitBlock.armorAtZero} onChange={(value) => onChange("armorAtZero", value)} customRenderer={(v) => StatDisplayExtra(v, { showModifier: isModifier })} />
            </FormFieldLabel>
            <FormFieldLabel label="Equipment Slots" justifyContent="end">
                <NumberField step={1} value={outfitBlock.slots} onChange={(value) => onChange("slots", value)} customRenderer={(v) => StatDisplayExtra(v)} />
            </FormFieldLabel>
            <FormFieldLabel label="Acquire" justifyContent="end">
                <NumberField step={1} value={outfitBlock.acquire} onChange={(value) => onChange("acquire", value)} 
                    customRenderer={(v) => StatDisplayExtra(v, { showModifier: isModifier, suffix: "t" })} />
            </FormFieldLabel>
            <FormFieldLabel label="Knowledge" justifyContent="end">
                <NumberField step={1} value={outfitBlock.knowledge} onChange={(value) => onChange("knowledge", value)} 
                    customRenderer={(v) => StatDisplayExtra(v, { showModifier: isModifier, suffix: "kn" })} />
            </FormFieldLabel>
            <FormFieldLabel label="Influence" justifyContent="end">
                <NumberField step={1} value={outfitBlock.influence} onChange={(value) => onChange("influence", value)}
                    customRenderer={(v) => isModifier ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Resources" justifyContent="end">
                <NumberField step={1} value={outfitBlock.resources} onChange={(value) => onChange("resources", value)}
                    customRenderer={(v) => isModifier ? StatDisplay(v) : v} />
            </FormFieldLabel>
        </Stack>
        <ModalContentSection title="Outfit Type">
            <Stack direction="row">
                <TextField sx={{flexGrow: 1}} type="text" variant="standard" value={outfitType} onChange={(event) => setOutfitType(event.currentTarget.value)} />
                <Button onClick={handleAddType}>Add</Button>
            </Stack>
            <Stack flexWrap="wrap" useFlexGap direction="row" spacing={1}>
                {outfitBlock.outfitType.map((value, index) => <Chip onClick={() => handleRemoveType(index)} label={value} key={index} />)}
            </Stack>
        </ModalContentSection>
        <ModalContentSection title="Keywords">
            <Stack direction="row">
                <TextField sx={{flexGrow: 1}} type="text" variant="standard" value={feature} onChange={(event) => setFeature(event.currentTarget.value)} />
                <Button onClick={handleAddFeature}>Add</Button>
            </Stack>
            <Stack flexWrap="wrap" useFlexGap direction="row" spacing={1}>
                {outfitBlock.addFeatures.map((value, index) => <Chip onClick={() => handleRemoveFeature(index)} label={value} key={index} />)}
            </Stack>
        </ModalContentSection>
        <AbilityEditor title="Passive Abilities" abilities={outfitBlock.passives} setAbilities={(value) => onChange("passives", value)} />
        <Box paddingLeft={1}>
            <Typography>Notes</Typography>
            <TextField value={outfitBlock.notes} onChange={(event) => onChange('notes', event.currentTarget.value)} multiline fullWidth />
        </Box>
        <AbilityEditor title="Strong Hit Options" abilities={outfitBlock.strongHits} setAbilities={(value) => onChange("strongHits", value)} />
    </>
}

export const OutfitBlockEditorPage = (props: OutfitEditorProps) => {
    const { prefix, pathType, defaultName } = props;
    const { id } = useParams();
    const navigate = useNavigate();
    const [ current, setCurrent ] = useState<OutfitBlock>(loadLibraryData<OutfitBlock>(prefix, id ?? "NONE") ?? GetNew(defaultName));

    const handleSave = () => {
        navigate(`/library/${pathType}/${current.id}`, {replace: true});
        saveLibraryData(prefix, current);
    }

    const handleBack = () => {
        navigate("/library");
    }

    const handleExport = () => {
        exportData(current, prefix);
    }

    return <Page>
        <h2>{current.name}</h2>
        <FormGroup>
            <OutfitBlockEditor outfitBlock={current} setOutfitBlock={setCurrent} isModifier={props.isModifier} />
            <Stack direction={"row"} spacing={1} paddingTop={1}>
                <ButtonFlat onClick={handleSave}>Save</ButtonFlat>
                <ButtonFlat onClick={handleBack}>Back to Library</ButtonFlat>
                <ButtonFlat onClick={handleExport}>Export</ButtonFlat>
            </Stack>
        </FormGroup>
    </Page>
}